/**
 * rolloverUtils.ts
 *
 * Pure, side-effect-free utilities that implement the Cashflow Rollover
 * (גלגול) logic — including the new "Snooze" (דחייה) feature.
 *
 * ─── Business Rules ────────────────────────────────────────────────────────
 *
 * Rule 1 – Auto-rollover
 *   An action that is NOT reconciled (status !== 'matched') AND whose
 *   original date is in the past AND disable_rollover is false is "rolling".
 *   Its effective date = GREATEST(action_date, snoozed_until ?? action_date, TODAY)
 *   which simplifies to: MAX(snoozed_until ?? action_date, TODAY)
 *
 * Rule 2 – Stop-rollover toggle (disable_rollover = TRUE)
 *   The action stays pinned to its original `action_date`.
 *   Snooze is irrelevant when this flag is set.
 *
 * Rule 3 – Snooze (snoozed_until IS NOT NULL)
 *   The action has been "snoozed" to a future date by the user.
 *   Effective date = GREATEST(action_date, snoozed_until, TODAY)
 *   → While TODAY < snoozed_until: pinned to snoozed_until (shows as snoozed).
 *   → Once TODAY >= snoozed_until: effective date = TODAY (resumes rolling normally).
 *   → Note: is_rolling remains TRUE throughout — the snooze does not cancel rolling.
 *
 * Rule 4 – Offset / budget calculation still uses the ORIGINAL date
 *   `getEffectiveDate` must NOT be used when determining which offset period
 *   an action belongs to.  offsetCalculator.ts always reads `action_date`.
 *
 * ─── "Bank is Truth" Rules (used by buildUnifiedRows) ──────────────────────
 *
 * Rule B1 – Last Bank Date (נקודת חיתוך)
 *   The maximum `transaction_date` across all bank_transactions for the company.
 *
 * Rule B2 – Past zone (originalDate <= lastBankDate)
 *   Only bank transactions shown.  Expected rows hidden.
 *
 * Rule B3 – Rollover to Effective Date
 *   Expected rows that: originalDate <= lastBankDate AND status !== 'matched'
 *   AND disable_rollover !== true → sourceType = 'RolledOver'.
 *   effectiveDate = GREATEST(originalDate, snoozed_until ?? originalDate, TODAY)
 *
 * Rule B4 – Future zone (originalDate > lastBankDate)
 *   Expected rows shown on scheduled dates.
 */

import type { BankTransaction, RecurringActionLine, CashflowMapping } from '../types/database';
import type { KnownAction } from './knownActionsService';
import type { OffsetResult } from './offsetCalculator';

// ─── Date helpers ─────────────────────────────────────────────────────────────

/**
 * Returns today's date as a 'YYYY-MM-DD' string (local timezone, no time).
 */
export function getTodayString(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Returns true if `dateStr` ('YYYY-MM-DD') is strictly before today (local tz).
 */
function isPast(dateStr: string): boolean {
  return dateStr < getTodayString();
}

// ─── Core helpers ─────────────────────────────────────────────────────────────

/**
 * The minimal shape required from an action record.
 * Both `KnownAction` and `RecurringActionLine` satisfy this.
 */
export interface RolloverTarget {
  action_date:      string;
  status:           'pending' | 'matched' | 'ignored';
  disable_rollover?: boolean;
  /** Snooze date — keeps effective date at MAX(snoozed_until, TODAY) while active. */
  snoozed_until?:   string | null;
}

/**
 * Returns true when the action is currently "rolling":
 *   - status is NOT 'matched' or 'ignored'
 *   - disable_rollover is falsy
 *   - original action_date is in the past  (OR snoozed_until is set and in
 *     the past / present — snooze is implicitly a rollover)
 */
export function isRolling(action: RolloverTarget): boolean {
  if (action.status === 'matched') return false;
  if (action.status === 'ignored') return false;
  if (action.disable_rollover)     return false;
  // An action with a snoozed_until in the future is technically "being managed"
  // — it still counts as rolling (just pinned to a future date rather than today).
  if (action.snoozed_until) return true;
  return isPast(action.action_date);
}

/**
 * Returns true when the action is currently snoozed to a FUTURE date.
 * (snoozed_until > today AND disable_rollover is off AND not matched/ignored)
 */
export function isSnoozed(action: RolloverTarget): boolean {
  if (!action.snoozed_until)       return false;
  if (action.status === 'matched') return false;
  if (action.status === 'ignored') return false;
  if (action.disable_rollover)     return false;
  return action.snoozed_until > getTodayString();
}

/**
 * Core date arithmetic: GREATEST(action_date, snoozed_until ?? '', TODAY)
 * This is the single source of truth for where an action appears on the
 * cashflow timeline.
 *
 * @param originalDate  'YYYY-MM-DD' stored action_date
 * @param snoozedUntil  'YYYY-MM-DD' | null | undefined
 * @returns             effective 'YYYY-MM-DD'
 */
export function computeEffectiveDate(
  originalDate: string,
  snoozedUntil: string | null | undefined,
): string {
  const today = getTodayString();
  // GREATEST(originalDate, snoozed_until, TODAY)
  let best = originalDate > today ? originalDate : today;   // MAX(originalDate, today)
  if (snoozedUntil && snoozedUntil > best) {
    best = snoozedUntil;
  }
  return best;
}

/**
 * Returns the **effective** date ('YYYY-MM-DD') for positioning this action
 * on the cashflow timeline.
 *
 *  - disable_rollover = true  → original action_date (pinned, no rolling)
 *  - rolling with snooze      → GREATEST(action_date, snoozed_until, TODAY)
 *  - rolling without snooze   → GREATEST(action_date, TODAY)  = TODAY (for past dates)
 *  - not rolling              → original action_date
 *
 * IMPORTANT: Do NOT use for offset/budget period calculations.
 */
export function getEffectiveDate(action: RolloverTarget): string {
  if (action.disable_rollover) return action.action_date;
  if (!isRolling(action))      return action.action_date;
  return computeEffectiveDate(action.action_date, action.snoozed_until);
}

/**
 * Human-readable rollover label for UI tooltips / badges.
 */
export function getRolloverLabel(action: RolloverTarget): string | null {
  if (action.disable_rollover)          return 'גלגול מושבת';
  if (isSnoozed(action))                return `נדחה עד ${action.snoozed_until}`;
  if (isRolling(action))                return 'מגולגל להיום';
  return null;
}

// ─── Bank-is-Truth pipeline types ────────────────────────────────────────────

export type ForecastSourceType = 'Bank' | 'Known' | 'Recurring' | 'RolledOver';

export interface ForecastRow {
  key:                    string;
  id:                     string;
  rowNumber:              number;
  sourceType:             ForecastSourceType;
  effectiveDate:          string;
  originalDate:           string;
  /** When the row is snoozed, this holds the snooze target date. */
  snoozedUntil?:          string | null;
  description:            string;
  bankAccountNickname:    string;
  accountingCategoryName: string;
  cashflowMapping:        CashflowMapping | string | null;
  amount:                 number;
  isOffset:               boolean;
  offsetAmount:           number;
  rawAmount:              number;
  runningBalance:         number;
  status:                 string;
  ignoredByReconciliation: boolean;
}

// ─── Bank-is-Truth helpers ────────────────────────────────────────────────────

export function getLastBankDate(bankTxns: BankTransaction[]): string {
  if (bankTxns.length === 0) return '';
  return bankTxns.reduce<string>(
    (max, tx) => tx.transaction_date > max ? tx.transaction_date : max,
    '',
  );
}

export interface BuildUnifiedRowsOptions {
  bankTxns:       BankTransaction[];
  knownActions:   KnownAction[];
  recurringLines: RecurringActionLine[];
  offsetMap:      Map<string, OffsetResult>;
}

/**
 * Build unified `ForecastRow[]` applying the "Bank is Truth" rules +
 * the new snooze-aware GREATEST() effective-date logic.
 *
 *  Zone 1 — Past (originalDate <= lastBankDate)
 *    → ALL bank transactions
 *    → Expected rows SUPPRESSED unless qualifying for Zone 2
 *
 *  Zone 2 — Rollover (originalDate <= lastBankDate AND unmatched AND rollover-on)
 *    → effectiveDate = GREATEST(originalDate, snoozed_until, TODAY)
 *    → sourceType = 'RolledOver'
 *
 *  Zone 3 — Future (originalDate > lastBankDate)
 *    → Expected rows on scheduled dates (no rolling needed)
 *
 *  No-cutoff fallback — no bank transactions:
 *    Simple rollover; expectedRows use getEffectiveDate() as before.
 */
export function buildUnifiedRows(opts: BuildUnifiedRowsOptions): ForecastRow[] {
  const { bankTxns, knownActions, recurringLines, offsetMap } = opts;
  const lastBankDate = getLastBankDate(bankTxns);
  const hasCutoff    = lastBankDate !== '';

  const rows: ForecastRow[] = [];

  // ── Zone 1: All bank transactions ────────────────────────────────────────
  for (const tx of bankTxns) {
    rows.push({
      key:                     `bank-${tx.id}`,
      id:                      tx.id,
      rowNumber:               0,
      sourceType:              'Bank',
      effectiveDate:           tx.transaction_date,
      originalDate:            tx.transaction_date,
      snoozedUntil:            null,
      description:             tx.description,
      bankAccountNickname:     tx.bank_accounts?.nickname              ?? '—',
      accountingCategoryName:  tx.accounting_categories?.name         ?? '—',
      cashflowMapping:         tx.accounting_categories?.cashflow_mapping ?? tx.classification ?? null,
      amount:                  tx.amount,
      isOffset:                false,
      offsetAmount:            0,
      rawAmount:               tx.amount,
      runningBalance:          0,
      status:                  tx.status,
      ignoredByReconciliation: tx.status === 'ignored',
    });
  }

  // ── Known actions ─────────────────────────────────────────────────────────
  for (const ka of knownActions) {
    if (ka.status === 'ignored') continue;

    const originalDate = ka.action_date;

    if (hasCutoff && originalDate <= lastBankDate) {
      const rollable = ka.status !== 'matched' && !ka.disable_rollover;
      if (!rollable) continue;

      // Zone 2: roll forward — GREATEST(originalDate, snoozed_until, TODAY)
      const effectiveDate = computeEffectiveDate(originalDate, ka.snoozed_until);
      rows.push({
        key:                     `known-${ka.id}`,
        id:                      ka.id,
        rowNumber:               0,
        sourceType:              'RolledOver',
        effectiveDate,
        originalDate,
        snoozedUntil:            ka.snoozed_until ?? null,
        description:             ka.description,
        bankAccountNickname:     ka.bank_accounts?.nickname              ?? '—',
        accountingCategoryName:  ka.accounting_categories?.name         ?? '—',
        cashflowMapping:         ka.accounting_categories?.cashflow_mapping ?? null,
        amount:                  ka.amount,
        isOffset:                false,
        offsetAmount:            0,
        rawAmount:               ka.amount,
        runningBalance:          0,
        status:                  ka.status,
        ignoredByReconciliation: false,
      });
      continue;
    }

    // Zone 3 / no-cutoff fallback
    const effectiveDate = hasCutoff
      ? originalDate
      : getEffectiveDate(ka);
    const rolling = !hasCutoff && isRolling(ka);

    rows.push({
      key:                     `known-${ka.id}`,
      id:                      ka.id,
      rowNumber:               0,
      sourceType:              rolling ? 'RolledOver' : 'Known',
      effectiveDate,
      originalDate,
      snoozedUntil:            ka.snoozed_until ?? null,
      description:             ka.description,
      bankAccountNickname:     ka.bank_accounts?.nickname              ?? '—',
      accountingCategoryName:  ka.accounting_categories?.name         ?? '—',
      cashflowMapping:         ka.accounting_categories?.cashflow_mapping ?? null,
      amount:                  ka.amount,
      isOffset:                false,
      offsetAmount:            0,
      rawAmount:               ka.amount,
      runningBalance:          0,
      status:                  ka.status,
      ignoredByReconciliation: false,
    });
  }

  // ── Recurring action lines ────────────────────────────────────────────────
  for (const ra of recurringLines) {
    if (ra.status === 'ignored') continue;

    const originalDate = ra.action_date;
    const res          = offsetMap.get(ra.id);
    const displayAmt   = res ? res.displayAmount : ra.amount;

    if (hasCutoff && originalDate <= lastBankDate) {
      const rollable = ra.status !== 'matched' && !ra.disable_rollover;
      if (!rollable) continue;

      // Zone 2: roll forward — GREATEST(originalDate, snoozed_until, TODAY)
      const effectiveDate = computeEffectiveDate(originalDate, ra.snoozed_until);
      rows.push({
        key:                     `recurring-${ra.id}`,
        id:                      ra.id,
        rowNumber:               0,
        sourceType:              'RolledOver',
        effectiveDate,
        originalDate,
        snoozedUntil:            ra.snoozed_until ?? null,
        description:             ra.description,
        bankAccountNickname:     ra.bank_accounts?.nickname              ?? '—',
        accountingCategoryName:  ra.accounting_categories?.name         ?? '—',
        cashflowMapping:         ra.accounting_categories?.cashflow_mapping ?? null,
        amount:                  displayAmt,
        isOffset:                res?.isOffset    ?? false,
        offsetAmount:            res?.offsetAmount ?? 0,
        rawAmount:               res?.rawAmount    ?? ra.amount,
        runningBalance:          0,
        status:                  ra.status,
        ignoredByReconciliation: false,
      });
      continue;
    }

    // Zone 3 / no-cutoff fallback
    const effectiveDate = hasCutoff
      ? originalDate
      : getEffectiveDate(ra);
    const rolling = !hasCutoff && isRolling(ra);

    rows.push({
      key:                     `recurring-${ra.id}`,
      id:                      ra.id,
      rowNumber:               0,
      sourceType:              rolling ? 'RolledOver' : 'Recurring',
      effectiveDate,
      originalDate,
      snoozedUntil:            ra.snoozed_until ?? null,
      description:             ra.description,
      bankAccountNickname:     ra.bank_accounts?.nickname              ?? '—',
      accountingCategoryName:  ra.accounting_categories?.name         ?? '—',
      cashflowMapping:         ra.accounting_categories?.cashflow_mapping ?? null,
      amount:                  displayAmt,
      isOffset:                res?.isOffset    ?? false,
      offsetAmount:            res?.offsetAmount ?? 0,
      rawAmount:               res?.rawAmount    ?? ra.amount,
      runningBalance:          0,
      status:                  ra.status,
      ignoredByReconciliation: false,
    });
  }

  return rows;
}
